import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNearbyStrikes,
  fetchQuickOrderAccounts,
  placeQuickOrder,
} from '../api';

const REFRESH_INTERVAL = 5000;

/**
 * Quick Order — minimal one-screen order entry.
 * Paper accounts fire instantly. Live accounts get a one-tap inline confirm.
 * For positions / exits, use the Positions page.
 */
export default function QuickOrder() {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [index, setIndex] = useState('Nifty');

  const [strikes, setStrikes] = useState([]);
  const [spotPrice, setSpotPrice] = useState(0);

  const [side, setSide] = useState('SELL');
  const [optionType, setOptionType] = useState('CE');
  const [selectedStrike, setSelectedStrike] = useState('');
  const [customStrike, setCustomStrike] = useState('');
  const [lots, setLots] = useState(1);
  const [slType, setSlType] = useState('none');
  const [slValue, setSlValue] = useState('');
  const [showSL, setShowSL] = useState(false);

  const [basket, setBasket] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);

  const stripRef = useRef(null);
  const atmRef = useRef(null);

  // ── Loaders ───────────────────────────────────────────────────────
  const loadAccounts = useCallback(async () => {
    try {
      const res = await fetchQuickOrderAccounts();
      setAccounts(res.data?.accounts || []);
    } catch {}
  }, []);

  const loadStrikes = useCallback(async () => {
    try {
      const res = await fetchNearbyStrikes(index, 6);
      if (res.data?.status === 'ok') {
        setStrikes(res.data.strikes || []);
        setSpotPrice(res.data.spot_price || 0);
      }
    } catch {}
  }, [index]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadStrikes(); }, [loadStrikes]);
  useEffect(() => {
    const id = setInterval(loadStrikes, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [loadStrikes]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3500);
    return () => clearTimeout(t);
  }, [message]);

  // Center ATM in strip when strikes load / change
  useEffect(() => {
    if (atmRef.current && stripRef.current) {
      const el = atmRef.current;
      const parent = stripRef.current;
      parent.scrollLeft = el.offsetLeft - parent.clientWidth / 2 + el.clientWidth / 2;
    }
  }, [strikes]);

  // ── Derived ───────────────────────────────────────────────────────
  const currentAccount = useMemo(
    () => accounts.find(a => (a.id ?? null) === accountId) || accounts[0] || null,
    [accounts, accountId]
  );
  const isPaper = currentAccount ? currentAccount.paper_trading : true;

  const activeStrike = customStrike ? parseFloat(customStrike)
                     : selectedStrike ? parseFloat(selectedStrike) : 0;

  const buildLeg = () => {
    if (!activeStrike) return null;
    return {
      strike: activeStrike,
      option_type: optionType,
      side,
      lots,
      sl_type: slType,
      sl_value: slValue ? parseFloat(slValue) : 0,
    };
  };

  const clearForm = () => {
    setSelectedStrike(''); setCustomStrike(''); setSlValue('');
    setSlType('none'); setShowSL(false); setLots(1);
  };

  // ── Actions ───────────────────────────────────────────────────────
  const addToBasket = () => {
    const leg = buildLeg();
    if (!leg) { setMessage({ type: 'error', text: 'Pick a strike first' }); return; }
    setBasket(b => [...b, leg]);
    clearForm();
    setMessage({ type: 'success', text: `Added ${leg.side} ${leg.option_type} ${leg.strike}` });
  };

  const removeBasketLeg = (idx) => setBasket(b => b.filter((_, i) => i !== idx));
  const clearBasket = () => setBasket([]);

  const doPlace = async (legs) => {
    setLoading(true);
    setConfirmKind(null);
    try {
      const res = await placeQuickOrder({ account_id: accountId, index, legs });
      const d = res.data || {};
      setMessage({
        type: d.status === 'ok' ? 'success' : 'error',
        text: d.message || 'Order submitted',
      });
      if (d.status === 'ok' || d.status === 'partial') {
        clearForm(); clearBasket();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Order failed' });
    }
    setLoading(false);
  };

  const handlePlaceSingle = () => {
    const leg = buildLeg();
    if (!leg) { setMessage({ type: 'error', text: 'Pick a strike first' }); return; }
    if (isPaper) doPlace([leg]); else setConfirmKind('single');
  };

  const handlePlaceBasket = () => {
    if (!basket.length) return;
    if (isPaper) doPlace(basket); else setConfirmKind('basket');
  };

  const handleConfirm = () => {
    if (confirmKind === 'single') {
      const leg = buildLeg();
      if (leg) doPlace([leg]);
    } else if (confirmKind === 'basket') {
      doPlace(basket);
    }
  };

  // Adjust custom strike with +/-
  const stepStrike = (delta) => {
    const cur = activeStrike || Math.round(spotPrice / 50) * 50;
    const step = index === 'BankNifty' ? 100 : index === 'Sensex' ? 100 : 50;
    const next = cur + delta * step;
    setCustomStrike(String(next));
    setSelectedStrike('');
  };

  // ── Render ────────────────────────────────────────────────────────
  const canPlaceSingle = !!activeStrike && !loading;

  return (
    <div className="qo-page">
      {message && (
        <div className={`qo-toast qo-toast-${message.type}`} onClick={() => setMessage(null)}>
          {message.type === 'success' ? '✓' : '✕'} {message.text}
        </div>
      )}

      {/* ── Title row ── */}
      <div className="qo-header">
        <button className="qo-back" onClick={() => navigate('/strategies')}>←</button>
        <h1 className="qo-title">Quick Order</h1>
        {spotPrice > 0 && (
          <div className="qo-spot-inline">
            <span>{index} Spot</span>
            <strong>{spotPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
          </div>
        )}
      </div>

      {/* ── Big, visible Account + Index bar ── */}
      <div className="qo-context">
        <div className="qo-ctx-block">
          <label>ACCOUNT</label>
          <div className="qo-account-pills">
            {accounts.map(a => {
              const aid = a.id ?? null;
              const active = (accountId ?? null) === aid;
              return (
                <button key={a.id ?? 'paper'}
                  className={`qo-acct-pill ${active ? 'on' : ''} ${a.paper_trading ? 'paper' : 'live'}`}
                  onClick={() => setAccountId(aid)}>
                  <span className="qo-acct-name">{a.name}</span>
                  <span className="qo-acct-tag">{a.paper_trading ? 'PAPER' : 'LIVE'}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="qo-ctx-block">
          <label>INDEX</label>
          <div className="qo-index-pills">
            {['Nifty', 'BankNifty', 'Sensex'].map(i => (
              <button key={i}
                className={`qo-idx-pill ${index === i ? 'on' : ''}`}
                onClick={() => setIndex(i)}>{i}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Order card ── */}
      <div className="qo-card qo-order">
        <div className="qo-row qo-row-pri">
          <div className="qo-pair">
            <button className={`qo-side sell ${side === 'SELL' ? 'on' : ''}`} onClick={() => setSide('SELL')}>SELL</button>
            <button className={`qo-side buy ${side === 'BUY' ? 'on' : ''}`} onClick={() => setSide('BUY')}>BUY</button>
          </div>
          <div className="qo-pair">
            <button className={`qo-opt ce ${optionType === 'CE' ? 'on' : ''}`} onClick={() => setOptionType('CE')}>CE</button>
            <button className={`qo-opt pe ${optionType === 'PE' ? 'on' : ''}`} onClick={() => setOptionType('PE')}>PE</button>
          </div>
          <div className="qo-lots">
            <span>Lots</span>
            <button onClick={() => setLots(Math.max(1, lots - 1))}>−</button>
            <input type="number" min="1" value={lots}
              onChange={e => setLots(Math.max(1, parseInt(e.target.value) || 1))} />
            <button onClick={() => setLots(lots + 1)}>+</button>
          </div>
        </div>

        {/* ── Friendly strike picker ── */}
        <div className="qo-strike-block">
          <div className="qo-strike-head">
            <span className="qo-strike-label">STRIKE</span>
            {activeStrike > 0 && spotPrice > 0 && (
              <span className="qo-strike-info">
                <strong>{activeStrike.toLocaleString()}</strong>
                <em>
                  {(() => {
                    const diff = activeStrike - spotPrice;
                    const isItm = optionType === 'CE' ? diff < 0 : diff > 0;
                    const isAtm = Math.abs(diff) < (index === 'BankNifty' || index === 'Sensex' ? 50 : 25);
                    const tag = isAtm ? 'ATM' : isItm ? 'ITM' : 'OTM';
                    const sign = diff > 0 ? '+' : '';
                    return `${tag} · ${sign}${diff.toFixed(0)} from spot`;
                  })()}
                </em>
              </span>
            )}
          </div>

          <div className="qo-strike-stepper">
            <button className="qo-step-btn" onClick={() => stepStrike(-1)} title="Lower strike">−</button>
            <div className="qo-strip" ref={stripRef}>
              {strikes.map(s => {
                const isAtm = s.label === 'ATM';
                const isSel = selectedStrike === String(s.strike);
                const isItm = (optionType === 'CE' ? s.diff < 0 : s.diff > 0);
                const tag = isAtm ? 'ATM' : isItm ? 'ITM' : 'OTM';
                return (
                  <button key={s.strike}
                    ref={isAtm ? atmRef : null}
                    className={`qo-chip ${isSel ? 'on' : ''} ${isAtm ? 'atm' : isItm ? 'itm' : 'otm'}`}
                    onClick={() => { setSelectedStrike(String(s.strike)); setCustomStrike(''); }}>
                    <span className="qo-chip-strike">{s.strike}</span>
                    <span className="qo-chip-tag">{tag}</span>
                  </button>
                );
              })}
            </div>
            <button className="qo-step-btn" onClick={() => stepStrike(1)} title="Higher strike">+</button>
          </div>

          <div className="qo-strike-extras">
            <input
              className="qo-custom"
              type="number"
              placeholder="Or type any strike…"
              value={customStrike}
              onChange={e => { setCustomStrike(e.target.value); setSelectedStrike(''); }}
            />
            <button
              className={`qo-sl-toggle ${slType !== 'none' ? 'on' : ''}`}
              onClick={() => {
                if (slType === 'none') { setSlType('option'); setShowSL(true); }
                else { setSlType('none'); setShowSL(false); setSlValue(''); }
              }}>
              {slType !== 'none'
                ? `SL: ${slType === 'spot' ? 'Spot' : 'Opt'} ${slValue || ''}`
                : '+ Stop Loss'}
            </button>
          </div>
        </div>

        {showSL && slType !== 'none' && (
          <div className="qo-sl-row">
            <div className="qo-pair">
              <button className={`qo-sl-btn ${slType === 'option' ? 'on' : ''}`} onClick={() => setSlType('option')}>Option Px</button>
              <button className={`qo-sl-btn ${slType === 'spot' ? 'on' : ''}`} onClick={() => setSlType('spot')}>Spot Px</button>
            </div>
            <input
              type="number" step="0.05"
              placeholder={slType === 'spot' ? Math.round(spotPrice || 0) : '150'}
              value={slValue} onChange={e => setSlValue(e.target.value)}
            />
          </div>
        )}

        {/* Primary action / inline live confirm */}
        {confirmKind === null ? (
          <div className="qo-actions">
            <button
              className={`qo-place ${side === 'SELL' ? 'sell' : 'buy'}`}
              disabled={!canPlaceSingle}
              onClick={handlePlaceSingle}>
              {side === 'SELL' ? '↓' : '↑'} {side} {activeStrike ? `${optionType} ${activeStrike}` : '— pick strike —'} × {lots}
            </button>
            <button className="qo-add" disabled={!activeStrike} onClick={addToBasket}>
              + Basket
            </button>
          </div>
        ) : (
          <div className="qo-confirm">
            <span><strong>LIVE</strong> — fire {confirmKind === 'single'
              ? `${side} ${optionType} ${activeStrike} × ${lots}`
              : `${basket.length} leg${basket.length > 1 ? 's' : ''}`}?</span>
            <button className="qo-confirm-yes" disabled={loading} onClick={handleConfirm}>
              {loading ? '…' : 'Yes, Fire'}
            </button>
            <button className="qo-confirm-no" onClick={() => setConfirmKind(null)}>Cancel</button>
          </div>
        )}

        {/* Basket — only when non-empty */}
        {basket.length > 0 && (
          <div className="qo-basket">
            <div className="qo-basket-head">
              <strong>Basket ({basket.length})</strong>
              <div>
                <button className="qo-basket-clear" onClick={clearBasket} disabled={loading}>Clear</button>
                <button className="qo-basket-fire" disabled={loading} onClick={handlePlaceBasket}>
                  Place All{!isPaper && ' (Live)'}
                </button>
              </div>
            </div>
            {basket.map((leg, idx) => (
              <div key={idx} className="qo-basket-leg">
                <span className={`qo-tag-side ${leg.side === 'SELL' ? 's' : 'b'}`}>{leg.side}</span>
                <span className={`qo-tag-opt ${leg.option_type === 'CE' ? 'ce' : 'pe'}`}>{leg.option_type}</span>
                <span className="qo-leg-strike">{leg.strike}</span>
                <span className="qo-leg-lots">× {leg.lots}</span>
                {leg.sl_type !== 'none' && (
                  <span className="qo-leg-sl">SL {leg.sl_type === 'spot' ? 'S' : 'O'} {leg.sl_value || '—'}</span>
                )}
                <button className="qo-leg-remove" onClick={() => removeBasketLeg(idx)}>×</button>
              </div>
            ))}
          </div>
        )}

        <p className="qo-foot-hint">
          Need to view or exit open positions? Go to <button className="qo-link" onClick={() => navigate('/positions')}>Positions</button>.
        </p>
      </div>
    </div>
  );
}
